import React, { useEffect } from 'react';

import {
  STREAM_DATA_TYPE_OPTIONS,
  CAMERA_ROTATION_OPTIONS,
  TIMESTAMP_SOURCE_OPTIONS,
} from '@vlabs/api-bindings/src/constants';
import validate from '@vlabs/shared/validators';
import { Control, SettingsItemWrapper } from '@vlabs/uikit';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import st from './SharedSubFormStyles.module.sass';

const DEFAULT_TIMESTAMP_SOURCE_VALUE = TIMESTAMP_SOURCE_OPTIONS.auto;

export function StreamDataSubForm() {
  const {
    register, formState: { errors }, control, setValue,
  } = useFormContext();
  const { t } = useTranslation();

  const dataType = useWatch({ name: 'data.type' })?.value;
  const timeStampSource = useWatch({ name: 'data.timestamp_source' })?.value;
  const isPtsTimestampSource = timeStampSource === 'pts';
  const isAutoTimestampSource = timeStampSource === 'auto';

  useEffect(() => {
    if (
      (dataType === 'stream' && timeStampSource === 'frame_rate')
        || (dataType === 'videofile' && timeStampSource === 'server')
    ) {
      setValue('data.timestamp_source', DEFAULT_TIMESTAMP_SOURCE_VALUE);
    }
  }, [dataType]);

  const commonStylesProps = {
    className: st.SettingItem,
    titleClassName: st.SettingItem__Title,
    contentClassName: st.SettingItem__Content,
  };

  return (
    <React.Fragment>
      <SettingsItemWrapper
        title={t('videoAnalytics:editAndCreateStreamsPageTitles.type')}
        {...commonStylesProps}
      >
        <Control.Select
          control={control}
          errors={errors}
          id="data.type"
          name="data.type"
          options={STREAM_DATA_TYPE_OPTIONS.raw}
          rules={{ required: validate.required() }}
        />
      </SettingsItemWrapper>

      <SettingsItemWrapper
        title={t('videoAnalytics:editAndCreateStreamsPageTitles.full_path_to_stream')}
        {...commonStylesProps}
      >
        <Control.Input
          {...register('data.reference', {
            required: validate.required(),
            maxLength: validate.maxLength(2048),
          })}
          errors={errors}
        />
      </SettingsItemWrapper>

      <SettingsItemWrapper
        data-tooltip-content={t('videoAnalytics:editAndCreateStreamsPageTitles.timing_format.tooltip')}
        title={t('videoAnalytics:editAndCreateStreamsPageTitles.timing_format.label')}
        {...commonStylesProps}
      >
        <Control.Select
          className={st.Select}
          control={control}
          id="data.timestamp_source"
          isOptionDisabled={(option) => {
            if (dataType === 'stream') return option.value === 'frame_rate';
            if (dataType === 'videofile') return option.value === 'server';
            return false;
          }}
          name="data.timestamp_source"
          options={TIMESTAMP_SOURCE_OPTIONS.raw}
        />
      </SettingsItemWrapper>

      <SettingsItemWrapper
        data-tooltip-content={t('videoAnalytics:editAndCreateStreamsPageTitles.start_time.tooltip')}
        title={t('videoAnalytics:editAndCreateStreamsPageTitles.start_time.label')}
        {...commonStylesProps}
      >
        <Control.Input
          disabled={!isPtsTimestampSource && !isAutoTimestampSource}
          errors={errors}
          {...register('data.pts.start_time', {
            validate: validate.number(),
          })}
        />
      </SettingsItemWrapper>

      <SettingsItemWrapper
        data-tooltip-content={t('videoAnalytics:editAndCreateStreamsPageTitles.camera_rotation_angle.tooltip')}
        title={t('videoAnalytics:editAndCreateStreamsPageTitles.camera_rotation_angle.label')}
        {...commonStylesProps}
      >
        <Control.Select
          control={control}
          errors={errors}
          id="data.rotation"
          name="data.rotation"
          options={CAMERA_ROTATION_OPTIONS.raw}
        />
      </SettingsItemWrapper>
    </React.Fragment>
  );
}
